// The weekly decision deck — 20 cards across five financial frameworks. Each card
// is a real dilemma with three choices; every choice schedules a DELAYED
// consequence (a few weeks out) so players feel that good calls pay off later and
// bad calls cost later. The framework tag + one-line lesson are shown when it lands.

import type { ConsequenceEffect } from '@biz-kid/engine';

export type Framework = 'Cashflow' | 'Compounding' | 'Risk' | 'Leverage' | 'Reputation';

export const FRAMEWORKS: Record<Framework, { label: string; icon: string; color: string }> = {
  Cashflow: { label: 'Cashflow', icon: '💧', color: 'var(--info)' },
  Compounding: { label: 'Compounding', icon: '❄️', color: 'var(--up)' },
  Risk: { label: 'Risk', icon: '🎲', color: 'var(--warn)' },
  Leverage: { label: 'Leverage', icon: '🏗️', color: 'var(--brand)' },
  Reputation: { label: 'Reputation', icon: '🤝', color: 'var(--down)' },
};

export type DecisionChoice = {
  label: string;
  sub: string;
  immediate?: ConsequenceEffect;
  delayed: { weeksAhead: number; lesson: string; effect: ConsequenceEffect };
};

export type DecisionCard = {
  id: string;
  framework: Framework;
  icon: string;
  title: string;
  situation: string;
  choices: DecisionChoice[];
};

export const DECK: DecisionCard[] = [
  // ── Cashflow ──────────────────────────────────────────────────────────────
  {
    id: 'bulk-discount',
    framework: 'Cashflow',
    icon: '📦',
    title: 'Bulk discount',
    situation: 'A supplier offers 40% off — but only if you buy a HUGE batch now. It would wipe out your cash reserves.',
    choices: [
      { label: 'Take the whole batch', sub: 'Empty the till for the deal', immediate: { cash: -80 }, delayed: { weeksAhead: 2, lesson: 'Cheaper stock lifted your margins — but running cash to zero is risky if trouble hits.', effect: { cash: 130 } } },
      { label: 'Buy a sensible amount', sub: 'Keep a cushion', immediate: { cash: -35 }, delayed: { weeksAhead: 2, lesson: 'Balance beats greed — a healthy cash cushion keeps you safe and still earning.', effect: { cash: 60 } } },
      { label: 'Skip the deal', sub: 'Cash is king', delayed: { weeksAhead: 2, lesson: 'You stayed liquid and safe — but a real bargain slipped past. Reserves are for using wisely.', effect: { cash: 0 } } },
    ],
  },
  {
    id: 'late-payer',
    framework: 'Cashflow',
    icon: '⏳',
    title: 'A customer wants credit',
    situation: 'A big customer wants to pay you next month instead of now. The order is large.',
    choices: [
      { label: 'Cash only, no credit', sub: 'Protect your cashflow', delayed: { weeksAhead: 2, lesson: 'Cash in hand keeps you running. You can’t spend a promise.', effect: { cash: 30 } } },
      { label: 'Give 2 weeks credit', sub: 'A fair middle', delayed: { weeksAhead: 2, lesson: 'Short, careful credit won the order without choking your cash.', effect: { cash: 55 } } },
      { label: 'Give a month, no deposit', sub: 'Win the big order', immediate: { cash: 20 }, delayed: { weeksAhead: 3, lesson: 'They paid late and you nearly ran dry. Big sales mean nothing if the cash arrives too late.', effect: { cash: -40 } } },
    ],
  },
  {
    id: 'shiny-upgrade',
    framework: 'Cashflow',
    icon: '✨',
    title: 'A flashy upgrade',
    situation: 'You could spend most of your cash on a flashy new sign that looks amazing but doesn’t earn anything.',
    choices: [
      { label: 'Buy the flashy sign', sub: 'Looks great!', immediate: { cash: -60 }, delayed: { weeksAhead: 2, lesson: 'It looked good but earned nothing — that’s a liability dressed up as an asset.', effect: { cash: -10 } } },
      { label: 'A modest sign', sub: 'Enough to be seen', immediate: { cash: -20 }, delayed: { weeksAhead: 2, lesson: 'Just enough did the job. Spend on what pays you back.', effect: { cash: 25 } } },
      { label: 'Put the cash to work', sub: 'Into the business', delayed: { weeksAhead: 2, lesson: 'Money working in the business beats money on the wall.', effect: { cash: 45 } } },
    ],
  },
  {
    id: 'emergency-fund',
    framework: 'Cashflow',
    icon: '🛟',
    title: 'Build a buffer?',
    situation: 'Things are going well. Do you set some cash aside as a safety buffer, or push it all into growth?',
    choices: [
      { label: 'Set aside a buffer', sub: 'Sleep easy', immediate: { cash: -25 }, delayed: { weeksAhead: 2, lesson: 'When a bad week came, your buffer carried you. That’s peace of mind.', effect: { cash: 40 } } },
      { label: 'Half and half', sub: 'A bit of both', delayed: { weeksAhead: 2, lesson: 'A little safety, a little growth — a sensible founder’s balance.', effect: { cash: 30 } } },
      { label: 'All into growth', sub: 'No brakes', delayed: { weeksAhead: 2, lesson: 'Growth is great until a surprise bill arrives with no cushion. Keep a buffer.', effect: { cash: -20 } } },
    ],
  },

  // ── Compounding ───────────────────────────────────────────────────────────
  {
    id: 'reinvest-reward',
    framework: 'Compounding',
    icon: '🌱',
    title: 'Reinvest or reward?',
    situation: 'You made a tidy profit this week. Reinvest it into the business, or treat yourself?',
    choices: [
      { label: 'Reinvest all of it', sub: 'Feed the snowball', immediate: { cash: -40 }, delayed: { weeksAhead: 2, lesson: 'Reinvested profit grows on itself — that’s how small money becomes big money.', effect: { cash: 75, demandMult: 1.1, demandWeeks: 2 } } },
      { label: 'Reinvest most, treat a little', sub: 'Earned a small reward', immediate: { cash: -10 }, delayed: { weeksAhead: 2, lesson: 'You fed the snowball and still enjoyed the win. Sustainable.', effect: { cash: 35 } } },
      { label: 'Spend it all on treats', sub: 'You earned it!', immediate: { cash: 5 }, delayed: { weeksAhead: 2, lesson: 'Fun now, but the snowball never got rolling. Profit you spend can’t compound.', effect: { cash: -5 } } },
    ],
  },
  {
    id: 'patience-payout',
    framework: 'Compounding',
    icon: '🪴',
    title: 'Cash out early?',
    situation: 'A small investment is slowly growing. You could cash it out now, or leave it to keep compounding.',
    choices: [
      { label: 'Leave it to grow', sub: 'Patience', delayed: { weeksAhead: 3, lesson: 'Time did the heavy lifting — compounding rewards the patient.', effect: { cash: 70 } } },
      { label: 'Take half out', sub: 'Lock in a little', immediate: { cash: 20 }, delayed: { weeksAhead: 3, lesson: 'A fair compromise — but the part you pulled stopped growing.', effect: { cash: 25 } } },
      { label: 'Cash it all out', sub: 'Money now', immediate: { cash: 40 }, delayed: { weeksAhead: 3, lesson: 'You grabbed it early and missed the best part of the curve. Let winners run.', effect: { cash: 0 } } },
    ],
  },
  {
    id: 'auto-save',
    framework: 'Compounding',
    icon: '🔁',
    title: 'Automate savings?',
    situation: 'You could set up a rule to tuck away a little every single week — automatically.',
    choices: [
      { label: 'Automate it', sub: 'Set and forget', immediate: { cash: -15 }, delayed: { weeksAhead: 3, lesson: 'Tiny, automatic, relentless — that’s how steady habits build real wealth.', effect: { cash: 60 } } },
      { label: 'Save when you remember', sub: 'Manually', delayed: { weeksAhead: 3, lesson: 'You saved sometimes. Better than nothing — but habits beat willpower.', effect: { cash: 20 } } },
      { label: 'Don’t bother', sub: 'Maybe later', delayed: { weeksAhead: 3, lesson: '“Later” never came. The earlier you start, the more compounding works for you.', effect: { cash: -5 } } },
    ],
  },
  {
    id: 'second-asset',
    framework: 'Compounding',
    icon: '🧱',
    title: 'Stack another asset?',
    situation: 'You already own one little income asset. Add a second now, or wait?',
    choices: [
      { label: 'Add a second asset', sub: 'Two streams', immediate: { cash: -45 }, delayed: { weeksAhead: 2, lesson: 'Two streams pay more than one — and they stack faster every time.', effect: { cash: 80 } } },
      { label: 'Wait and save more first', sub: 'Build the deposit', delayed: { weeksAhead: 2, lesson: 'Patience is fine, but assets bought sooner compound longer. Don’t wait too long.', effect: { cash: 25 } } },
      { label: 'One is enough', sub: 'Keep it simple', delayed: { weeksAhead: 2, lesson: 'Comfort is the enemy of growth. The wealthy keep stacking.', effect: { cash: 0 } } },
    ],
  },

  // ── Risk ──────────────────────────────────────────────────────────────────
  {
    id: 'market-crash',
    framework: 'Risk',
    icon: '📉',
    title: 'The market crashes',
    situation: 'Prices suddenly drop and your investment is down. Everyone around you is panic-selling.',
    choices: [
      { label: 'Hold steady', sub: 'Don’t panic', delayed: { weeksAhead: 2, lesson: 'You held through the dip and it recovered. Panic-selling locks in losses.', effect: { cash: 60 } } },
      { label: 'Buy a little more', sub: 'Things are on sale', immediate: { cash: -25 }, delayed: { weeksAhead: 2, lesson: 'Brave but smart — buying the dip paid off. Fear is when bargains appear.', effect: { cash: 80 } } },
      { label: 'Panic sell everything', sub: 'Get out now!', immediate: { cash: 30 }, delayed: { weeksAhead: 2, lesson: 'You sold at the bottom — then watched it bounce back without you. Ouch.', effect: { cash: -45 } } },
    ],
  },
  {
    id: 'all-eggs',
    framework: 'Risk',
    icon: '🧺',
    title: 'All in on one bet?',
    situation: 'A friend swears one single venture will make you rich. You could put everything into it.',
    choices: [
      { label: 'Spread it out', sub: 'Diversify', delayed: { weeksAhead: 2, lesson: 'Spreading your bets meant one flop didn’t sink you. Don’t risk it all on one thing.', effect: { cash: 40 } } },
      { label: 'A small bet only', sub: 'Test the water', immediate: { cash: -15 }, delayed: { weeksAhead: 2, lesson: 'You risked only what you could lose — and learned cheaply.', effect: { cash: 30 } } },
      { label: 'Bet it all', sub: 'Go big!', immediate: { cash: -50 }, delayed: { weeksAhead: 2, lesson: 'It didn’t pan out, and everything was on it. Never risk what you can’t afford to lose.', effect: { cash: -20 } } },
    ],
  },
  {
    id: 'insurance',
    framework: 'Risk',
    icon: '☂️',
    title: 'Insure your gear?',
    situation: 'You could pay a small amount to insure your setup against a crash or a hack.',
    choices: [
      { label: 'Get insured', sub: 'A small cost', immediate: { cash: -15 }, delayed: { weeksAhead: 2, lesson: 'A storm hit — and you were covered. Small, steady protection beats a big surprise loss.', effect: { cash: 35 } } },
      { label: 'Self-insure with savings', sub: 'Keep a fund instead', delayed: { weeksAhead: 2, lesson: 'Your own buffer absorbed it. Either way — be ready for the bad day.', effect: { cash: 15 } } },
      { label: 'Risk it', sub: 'Save the fee', immediate: { cash: 10 }, delayed: { weeksAhead: 2, lesson: 'No cover when trouble came — the saved fee cost you far more. Manage your risks.', effect: { cash: -30 } } },
    ],
  },
  {
    id: 'hot-tip',
    framework: 'Risk',
    icon: '🔥',
    title: 'A “can’t-lose” tip',
    situation: 'Someone promises a guaranteed, can’t-lose, double-your-money deal — if you act right now.',
    choices: [
      { label: 'Walk away', sub: 'Too good to be true', delayed: { weeksAhead: 2, lesson: 'It was a scam. “Guaranteed” and “act now” are red flags. You dodged it.', effect: { cash: 10 } } },
      { label: 'Research it first', sub: 'Slow down', delayed: { weeksAhead: 2, lesson: 'You checked, found the catch, and saved yourself. Always do your homework.', effect: { cash: 20 } } },
      { label: 'Jump in fast', sub: 'Before it’s gone!', immediate: { cash: -40 }, delayed: { weeksAhead: 2, lesson: 'It vanished with your money. If it sounds too good to be true, it is.', effect: { cash: 0 } } },
    ],
  },

  // ── Leverage ──────────────────────────────────────────────────────────────
  {
    id: 'hire-or-grind',
    framework: 'Leverage',
    icon: '🧑‍🍳',
    title: 'Hire or grind?',
    situation: 'Demand is booming and you can’t keep up alone. Hire a helper, or grind it out yourself?',
    choices: [
      { label: 'Hire a helper', sub: 'Pay now, sell more', immediate: { cash: -40 }, delayed: { weeksAhead: 2, lesson: 'More hands meant more sales than you could ever make alone. That’s leverage.', effect: { cash: 50, demandMult: 1.2, demandWeeks: 3 } } },
      { label: 'Grind it out solo', sub: 'Keep every cent', delayed: { weeksAhead: 2, lesson: 'You kept the wages but capped your sales — and tired yourself out. One person only has two hands.', effect: { cash: 15 } } },
      { label: 'Turn customers away', sub: 'Too busy', delayed: { weeksAhead: 2, lesson: 'Lost sales you’ll never get back. When demand outgrows you, grow your team.', effect: { cash: -10 } } },
    ],
  },
  {
    id: 'good-debt',
    framework: 'Leverage',
    icon: '🏦',
    title: 'Borrow to grow?',
    situation: 'A loan could buy equipment that doubles your output — but it’s debt you’ll repay with interest.',
    choices: [
      { label: 'Borrow for the equipment', sub: 'Good debt', immediate: { cash: 60 }, delayed: { weeksAhead: 3, lesson: 'The equipment earned more than the loan cost — that’s good debt working for you.', effect: { cash: -30, demandMult: 1.2, demandWeeks: 3 } } },
      { label: 'Save up and buy later', sub: 'No debt', delayed: { weeksAhead: 3, lesson: 'Slower, but you owe no one. Both paths can work — know which suits you.', effect: { cash: 25 } } },
      { label: 'Borrow for a flashy car', sub: 'Treat yourself', immediate: { cash: 60 }, delayed: { weeksAhead: 3, lesson: 'Bad debt: it earned nothing and the payments bit. Borrow for assets, not toys.', effect: { cash: -55 } } },
    ],
  },
  {
    id: 'systems',
    framework: 'Leverage',
    icon: '⚙️',
    title: 'Build a system?',
    situation: 'You could spend time building a simple system so your store partly runs itself.',
    choices: [
      { label: 'Build the system', sub: 'Work once, gain forever', immediate: { cash: -20 }, delayed: { weeksAhead: 2, lesson: 'The system kept paying long after you built it. Make your work multiply.', effect: { cash: 55, demandMult: 1.1, demandWeeks: 3 } } },
      { label: 'Do it manually for now', sub: 'No setup time', delayed: { weeksAhead: 2, lesson: 'Fine for today, but you’re trading time for money forever. Systems set you free.', effect: { cash: 15 } } },
      { label: 'Wing it', sub: 'No plan', delayed: { weeksAhead: 2, lesson: 'Chaos cost you sales. A little structure is leverage.', effect: { cash: -10 } } },
    ],
  },
  {
    id: 'partner',
    framework: 'Leverage',
    icon: '🤲',
    title: 'Take on a partner?',
    situation: 'A skilled friend offers to partner up — more reach, but you share the profits.',
    choices: [
      { label: 'Partner up', sub: 'Bigger together', delayed: { weeksAhead: 2, lesson: 'Sharing a bigger pie beat owning all of a small one. The right partner is leverage.', effect: { cash: 50, demandMult: 1.15, demandWeeks: 2 } } },
      { label: 'Just hire their help', sub: 'Keep ownership', immediate: { cash: -25 }, delayed: { weeksAhead: 2, lesson: 'You got the skills without giving up the business. A fair call.', effect: { cash: 35 } } },
      { label: 'Go it alone', sub: 'My way only', delayed: { weeksAhead: 2, lesson: 'You kept control but grew slower. Sometimes the right people speed you up.', effect: { cash: 10 } } },
    ],
  },

  // ── Reputation ────────────────────────────────────────────────────────────
  {
    id: 'giving',
    framework: 'Reputation',
    icon: '🎁',
    title: 'A giving opportunity',
    situation: 'The school fundraiser asks you to donate. It costs you now and earns nothing directly.',
    choices: [
      { label: 'Donate generously', sub: 'Give back', immediate: { cash: -30, reputation: 10 }, delayed: { weeksAhead: 2, lesson: 'Word spread that you’re generous — new customers came. Giving builds reputation that pays back.', effect: { cash: 45, demandMult: 1.15, demandWeeks: 2 } } },
      { label: 'Give a little', sub: 'A small gesture', immediate: { cash: -10, reputation: 4 }, delayed: { weeksAhead: 2, lesson: 'A small kindness, noticed. Generosity is a habit, not an amount.', effect: { cash: 20 } } },
      { label: 'Keep your money', sub: 'Not my problem', immediate: { reputation: -5 }, delayed: { weeksAhead: 2, lesson: 'People noticed you sat it out. Reputation is built — or lost — in small moments.', effect: { cash: -10 } } },
    ],
  },
  {
    id: 'unhappy-customer',
    framework: 'Reputation',
    icon: '😤',
    title: 'An unhappy customer',
    situation: 'A customer says their order was wrong and is telling everyone. Fixing it costs you.',
    choices: [
      { label: 'Refund and apologise', sub: 'Make it right', immediate: { cash: -20, reputation: 8 }, delayed: { weeksAhead: 2, lesson: 'They became your biggest fan and brought friends. Fixing mistakes fast builds trust.', effect: { cash: 35 } } },
      { label: 'Offer a small fix', sub: 'Meet halfway', immediate: { cash: -8, reputation: 3 }, delayed: { weeksAhead: 2, lesson: 'A fair fix kept the peace. Most people just want to be heard.', effect: { cash: 15 } } },
      { label: 'Ignore them', sub: 'They’ll get over it', immediate: { reputation: -12 }, delayed: { weeksAhead: 2, lesson: 'The bad word spread and sales dipped. One ignored complaint can cost many customers.', effect: { cash: -30 } } },
    ],
  },
  {
    id: 'honesty',
    framework: 'Reputation',
    icon: '⚖️',
    title: 'A pricing “mistake”',
    situation: 'You realise you accidentally overcharged a customer last week. They haven’t noticed.',
    choices: [
      { label: 'Tell them and refund', sub: 'Do the right thing', immediate: { cash: -15, reputation: 10 }, delayed: { weeksAhead: 2, lesson: 'Your honesty became your reputation — and people pay to deal with people they trust.', effect: { cash: 30 } } },
      { label: 'Credit it next time', sub: 'Quietly fix it', immediate: { reputation: 4 }, delayed: { weeksAhead: 2, lesson: 'You made it right in your own way. Integrity counts even when no one’s watching.', effect: { cash: 15 } } },
      { label: 'Say nothing', sub: 'Keep the extra', immediate: { cash: 15, reputation: -8 }, delayed: { weeksAhead: 2, lesson: 'It came out later and trust took a hit. A good name is worth more than a few coins.', effect: { cash: -25 } } },
    ],
  },
  {
    id: 'quality',
    framework: 'Reputation',
    icon: '🏅',
    title: 'Cut quality to save?',
    situation: 'Cheaper ingredients would save money — but customers might notice the difference.',
    choices: [
      { label: 'Keep the quality', sub: 'Protect your name', immediate: { cash: -10 }, delayed: { weeksAhead: 2, lesson: 'Loyal customers kept coming back for the quality. Reputation is your best marketing.', effect: { cash: 40, demandMult: 1.1, demandWeeks: 2 } } },
      { label: 'Cut costs a little', sub: 'A small trim', delayed: { weeksAhead: 2, lesson: 'A careful trim no one noticed — fine. But never cut what made people love you.', effect: { cash: 20 } } },
      { label: 'Go cheap', sub: 'More profit now', immediate: { cash: 20 }, delayed: { weeksAhead: 2, lesson: 'Customers noticed and drifted away. Short-term saving, long-term cost.', effect: { cash: -35 } } },
    ],
  },
];
